import { animated, SpringValue, SpringValues } from '@react-spring/web';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef } from 'react';
import { BlockRenderElement, Rect } from './types';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { DraggableCore, DraggableData, DraggableEvent, DraggableEventHandler } from 'react-draggable';
import { useUpdateEffect } from 'react-use';
import { useRequestAnimationFrame } from './utils/request-animation-frame.hook';
import { SectionThumbnail } from './components/section-thumbnail';
import { Nullable } from 'src/types/nullable.type';
import { ContentEditableInput } from './components/content-editable-input';
import { isCmdZ } from './utils/keyboard';
import { SectionThumbnailNew } from './components/section-thumbnail-new';
import { DragHandleSmallIcon, HiddenRegularIcon } from 'src/monet/icons';
import { useEditorStore } from './editor-store';

const THUMBNAIL_LAYOUT = {
  width: 56,
  height: 42,
  contentPadding: 2,
};

const SectionIndex = ({
  show,
  index,
  dragging,
  current,
  focused,
}: {
  show: boolean;
  index: number;
  dragging: boolean;
  current: boolean;
  focused: boolean;
}) => {
  let opacityClassNames = '';
  if (!show) {
    opacityClassNames = 'opacity-0';
  } else {
    if (dragging) {
      opacityClassNames = 'opacity-0';
    } else {
      if (current || focused) {
        opacityClassNames = 'opacity-100';
      } else {
        opacityClassNames = 'opacity-50 hover:opacity-100';
      }
    }
  }

  return (
    <div className={classNames('text-neue-journey-medium-strong transition text-neue-journey-bg', opacityClassNames)}>
      {index + 1}
    </div>
  );
};

type SectionCardProps = {
  id: string;
  index: number;
  // title: string;
  userProvidedTitle: Nullable<string>;
  // placeholder: boolean;
  current: boolean;
  expandedFraction: SpringValue<number>;
  sectionDrawerOpen: boolean;
  blockRenderElements: BlockRenderElement[];
  showIndices: boolean;
  dragging: boolean;
  elevated: boolean;
  focused: boolean;
  style: SpringValues<{ y: number }>;
  rect: Rect;
  readonly?: boolean;
  onCardClick: () => void;
  onCardDragStart: DraggableEventHandler;
  onCardDrag: DraggableEventHandler;
  onCardDragStop: () => void;
  onNameChange: (name: string) => void;
  setContextMenuState: (pageX: number, pageY: number, renameAction: () => void) => void;
};

export const SectionCard = React.forwardRef<HTMLDivElement, SectionCardProps>(
  (
    {
      id,
      index,
      // title,
      userProvidedTitle,
      current,
      expandedFraction,
      sectionDrawerOpen,
      blockRenderElements,
      showIndices,
      dragging,
      elevated,
      focused,
      style,
      rect,
      readonly = false,
      onCardClick,
      onCardDragStart,
      onCardDrag,
      onCardDragStop,
      onNameChange,
      setContextMenuState,
    }: SectionCardProps,
    ref
  ) => {
    const title = userProvidedTitle || '';
    // useRenderRateLogger('SectionCard');
    const [inputValue, setInputValue] = React.useState(title);
    const [editing, setEditing] = React.useState(false);
    const setFocusRef = React.useRef<() => void>(() => {});
    const setScrollToTopRef = React.useRef<() => void>(() => {});
    const sectionsHidden = useEditorStore((state) => state.sectionsHidden);
    const setSectionHidden = useEditorStore((state) => state.setSectionHidden);

    useEffect(() => {
      setInputValue(title);
    }, [title]);

    const updateName = useRefCallback(() => {
      if (inputValue !== title) {
        onNameChange(inputValue);
      }
    }, [inputValue, title, onNameChange]);

    const debouncedOnNameChange = useMemo(
      () =>
        debounce(() => {
          return updateName();
        }, 500),
      [onNameChange]
    );

    const setFocusIfNoTitle = useRefCallback(() => {
      // if (!userProvidedTitle && !derivedTitle) {
      setFocusRef.current();
      // }
    }, [userProvidedTitle]);

    useUpdateEffect(() => {
      if (editing) {
        setFocusIfNoTitle();
      } else {
        debouncedOnNameChange.cancel();
        updateName();
        setScrollToTopRef.current();
      }
    }, [editing, setFocusIfNoTitle]);

    const draggingActiveRef = useRef(false);
    const runOnRaf = useRequestAnimationFrame();

    const onDragStart = (e: DraggableEvent, args: DraggableData) => {
      // console.log('SectionCard: onDragStart', e.defaultPrevented);
      draggingActiveRef.current = true;
      onCardDragStart(e, args);
    };

    const onDrag = useRefCallback((e: DraggableEvent, args: DraggableData) => {
      runOnRaf(() => {
        if (draggingActiveRef.current) {
          onCardDrag(e, args);
        }
      });
    });

    // const throttledOnDrag = useMemo(
    //   () => throttle((e: DraggableEvent, args: DraggableData) => onDrag(e, args), 17),
    //   [onDrag]
    // );

    const onDragStop = () => {
      draggingActiveRef.current = false;
      onCardDragStop();
    };

    const effectiveTextValue = editing ? inputValue : title;

    useEffect(() => {
      const sectionId = blockRenderElements[0]?.section?.id;
      const sectionHidden = blockRenderElements[0]?.section?.hidden;

      setSectionHidden(sectionId, sectionHidden);
    }, []);

    return (
      <div className='absolute w-0 h-0'>
        <DraggableCore disabled={readonly} onStart={onDragStart} onDrag={onDrag} onStop={onDragStop} handle='.handle'>
          <animated.div
            className={classNames('group absolute flex items-center transition-opacity', {
              'cursor-grabbing': dragging,
              'relative z-neue-section-card-dragging': elevated,
            })}
            style={{
              transform: style.y.to((y) => `translate3d(${rect.x}px, ${y}px, 0)`),
              width: `${rect.width}px`,
              height: `${rect.height}px`,
            }}
            onClick={onCardClick}
            onContextMenu={(e) => {
              e.preventDefault(); // prevent the default behaviour when right clicked
              setContextMenuState(e.pageX, e.pageY, () => {
                setEditing(true);
                setFocusRef.current();
              });
            }}
            ref={ref}
          >
            <div className='shrink-0 w-6 h-full top-0 left-0 flex items-center justify-center'>
              <div
                className={classNames('w-6 handle flex items-center justify-center transition-opacity', {
                  'cursor-grabbing opacity-100': dragging,
                  'cursor-grab opacity-0 group-hover:opacity-100': !dragging,
                })}
              >
                <DragHandleSmallIcon className='text-neue-journey-bg' />
              </div>
            </div>
            <div className='flex gap-2 items-center'>
              <div
                className={classNames('handle transition-opacity relative', {
                  'opacity-100': current || dragging || focused,
                  'opacity-70 group-hover:opacity-100': !current && !dragging && !focused,
                  '-rotate-[4deg]': dragging,
                })}
              >
                <>
                  {sectionsHidden.find((item) => item.id === blockRenderElements[0]?.section?.id && item.hidden) && (
                    <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 opacity-80'>
                      <HiddenRegularIcon className=' w-6 h-6 text-black' />
                    </div>
                  )}

                  <SectionThumbnailNew
                    thumbnailLayout={THUMBNAIL_LAYOUT}
                    blockRenderElements={blockRenderElements}
                    layoutMode='web'
                    current={current}
                    className={classNames({ 'cursor-pointer': !dragging, 'cursor-grabbing': dragging })}
                  />
                </>
              </div>
              <animated.div
                className={classNames('flex flex-1 relative', {
                  'transition-opacity': dragging,
                })}
                style={{
                  opacity: dragging ? 0 : expandedFraction.to((fraction) => fraction),
                  transform: expandedFraction.to((fraction) => `translateX(-${(1 - fraction) * 8}px)`),
                }}
              >
                <ContentEditableInput
                  disabled={readonly}
                  setFocusRef={setFocusRef}
                  setScrollToTopRef={setScrollToTopRef}
                  className={classNames(
                    'neue-section-card-name flex-1 text-neue-journey-medium-strong line-clamp-2 transition text-neue-canvas-fg bg-transparent min-w-0',
                    {
                      'opacity-100': current,
                      'opacity-70 group-hover:opacity-100': !current && effectiveTextValue.length > 0,
                    }
                  )}
                  placeholder='Untitled'
                  value={effectiveTextValue}
                  onChange={(value) => {
                    setInputValue(value);
                    debouncedOnNameChange();
                  }}
                  onKeyDown={(e) => {
                    if (isCmdZ(e.nativeEvent)) {
                      debouncedOnNameChange.cancel();
                      const newTitle = (e.target as HTMLDivElement).innerText || '';
                      if (newTitle !== userProvidedTitle) {
                        onNameChange(newTitle);
                      }
                    }
                  }}
                  onEditStart={() => setEditing(true)}
                  onEditEnd={() => setEditing(false)}
                />
                {!editing && (
                  <div
                    className={classNames('absolute inset-0', {
                      'cursor-text': !dragging,
                      'cursor-grabbing': dragging,
                    })}
                    onClick={() => setFocusRef.current()}
                  ></div>
                )}
              </animated.div>
            </div>
          </animated.div>
        </DraggableCore>
      </div>
    );
  }
);
